<template>
    <div class="country_select_dropdown" ref="dropdown">
        <div class="input_xs" @click="toggleDropdown" :class="{ 'active': isShow }">
            <span class="txt">{{ selectedCountryText }}</span>
            <span class="chevron"><v-icon>mdi-chevron-down</v-icon></span>
        </div>
        <div class="dropdown_list" v-show="isShow" ref="dropdownList" :style="dropdownStyle">
            <div class="search_box">
                <input type="text" 
                       class="search_input" 
                       v-model="searchQuery" 
                       @input="filterCountries"
                       @keydown="handleKeydown"
                       placeholder="국가명 또는 번호"
                       ref="searchInput">
            </div>
            <div class="list_container">
                <div class="list_item" 
                     v-for="(country, index) in filteredCountries" 
                     :key="country.countryId"
                     @click="selectCountry(country)"
                     @mouseenter="selectedIndex = index"
                     :class="{ 
                         'selected': country.countryId === value,
                         'highlighted': index === selectedIndex 
                     }">
                    <span class="country_info">
                        (+{{ country.internationalDialingCodes }}) {{ getCountryName(country) }}
                    </span>
                </div>
                <div v-if="filteredCountries.length === 0" class="no_result">
                    결과 없음
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    name: 'CountrySelectDropdown',
    props: {
        value: {
            // 국가 목록 로드 전에는 선택값이 undefined 다 (기본값은 로드 후 KR 로 채워짐)
            type: String,
            default: undefined
        },
        countries: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            isShow: false,
            searchQuery: '',
            filteredCountries: [],
            dropdownPosition: {
                top: 0,
                left: 0,
                width: 0
            },
            openUpward: false,
            selectedIndex: -1
        };
    },
    computed: {
        ...mapState(['SaaS']),
        selectedCountry() {
            return this.countries.find(c => c.countryId === this.value);
        },
        selectedCountryName() {
            return this.getCountryName(this.selectedCountry);
        },
        selectedCountryText() {
            if (this.selectedCountry) {
                return `(+${this.selectedCountry.internationalDialingCodes}) ${this.selectedCountryName}`;
            }
            return '국가 선택';
        },
        dropdownStyle() {
            return {
                position: 'fixed',
                top: `${this.dropdownPosition.top}px`,
                left: `${this.dropdownPosition.left}px`,
                width: `${this.dropdownPosition.width}px`
            };
        }
    },
    watch: {
        countries: {
            immediate: true,
            handler(newVal) {
                this.filteredCountries = [...newVal];
            }
        },
        isShow(newVal) {
            if (newVal) {
                this.$nextTick(() => {
                    this.$refs.searchInput?.focus();
                    // 현재 선택된 국가가 있으면 해당 인덱스로 설정
                    if (this.value) {
                        const currentIndex = this.filteredCountries.findIndex(c => c.countryId === this.value);
                        if (currentIndex !== -1) {
                            this.selectedIndex = currentIndex;
                        }
                    }
                });
            } else {
                this.searchQuery = '';
                this.filterCountries();
                this.openUpward = false;
                this.selectedIndex = -1;
            }
        }
    },
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
        window.addEventListener('scroll', this.handleScroll, true);
        window.addEventListener('resize', this.handleResize);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleClickOutside);
        window.removeEventListener('scroll', this.handleScroll, true);
        window.removeEventListener('resize', this.handleResize);
    },
    methods: {
        getCountryName(country) {
            if (this.SaaS.locale.includes('en')) {
                return country.englishCountryName;
            }
            return country.koreanCountryName;
        },
        toggleDropdown() {
            if (!this.isShow) {
                this.updateDropdownPosition();
            }
            this.isShow = !this.isShow;
        },
        updateDropdownPosition() {
            if (!this.$refs.dropdown) return;
            
            const inputEl = this.$refs.dropdown.querySelector('.input_xs');
            if (inputEl) {
                const rect = inputEl.getBoundingClientRect();
                const dropdownHeight = 350; // 드롭다운 최대 높이
                const viewportHeight = window.innerHeight;
                const spaceBelow = viewportHeight - rect.bottom;
                const spaceAbove = rect.top;
                
                // 아래 공간이 부족하고 위 공간이 더 크면 위로 열기
                this.openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
                
                this.dropdownPosition = {
                    top: this.openUpward ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
                    left: rect.left,
                    width: rect.width
                };
            }
        },
        selectCountry(country) {
            this.$emit('input', country.countryId);
            this.isShow = false;
        },
        filterCountries() {
            const query = this.searchQuery.toLowerCase().trim();
            
            if (!query) {
                this.filteredCountries = [...this.countries];
            } else {
                this.filteredCountries = this.countries.filter(country => {
                    const countryName = country.koreanCountryName.toLowerCase();
                    const dialingCode = country.internationalDialingCodes.toString();
                    
                    return countryName.includes(query) || dialingCode.includes(query);
                });
            }
            
            // 검색 결과가 변경되면 선택 인덱스 초기화
            this.selectedIndex = -1;
            
            // 검색 결과가 변경되면 다음 틱에서 위치 업데이트
            this.$nextTick(() => {
                if (this.isShow) {
                    this.updateDropdownPositionAfterFilter();
                }
            });
        },
        updateDropdownPositionAfterFilter() {
            const inputEl = this.$refs.dropdown.querySelector('.input_xs');
            const dropdownEl = this.$refs.dropdownList;
            
            if (inputEl && dropdownEl) {
                const rect = inputEl.getBoundingClientRect();
                
                // 실제 드롭다운 높이 계산 (검색 결과에 따라 동적)
                const actualHeight = Math.min(dropdownEl.scrollHeight, 350);
                
                if (this.openUpward) {
                    // 위쪽으로 열린 경우, input 바로 위에 붙도록 조정
                    this.dropdownPosition.top = rect.top - actualHeight - 4;
                }
            }
        },
        handleClickOutside(event) {
            if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target) && 
                this.$refs.dropdownList && !this.$refs.dropdownList.contains(event.target)) {
                this.isShow = false;
            }
        },
        handleScroll() {
            if (this.isShow) {
                this.updateDropdownPosition();
            }
        },
        handleResize() {
            if (this.isShow) {
                this.updateDropdownPosition();
            }
        },
        handleKeydown(event) {
            switch(event.key) {
                case 'Escape':
                    event.preventDefault();
                    this.isShow = false;
                    break;
                    
                case 'ArrowDown':
                    event.preventDefault();
                    if (this.selectedIndex === -1) {
                        // 아무것도 선택되지 않은 경우 첫 번째 항목 선택
                        this.selectedIndex = 0;
                        this.scrollToHighlighted();
                    } else if (this.selectedIndex < this.filteredCountries.length - 1) {
                        this.selectedIndex++;
                        this.scrollToHighlighted();
                    }
                    break;
                    
                case 'ArrowUp':
                    event.preventDefault();
                    if (this.selectedIndex > 0) {
                        this.selectedIndex--;
                        this.scrollToHighlighted();
                    } else if (this.selectedIndex === -1 && this.filteredCountries.length > 0) {
                        // 아무것도 선택되지 않은 경우 마지막 항목 선택
                        this.selectedIndex = this.filteredCountries.length - 1;
                        this.scrollToHighlighted();
                    }
                    break;
                    
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedIndex >= 0 && this.selectedIndex < this.filteredCountries.length) {
                        this.selectCountry(this.filteredCountries[this.selectedIndex]);
                    } else if (this.selectedIndex === -1 && this.filteredCountries.length === 1) {
                        // 검색 결과가 하나뿐인 경우 자동 선택
                        this.selectCountry(this.filteredCountries[0]);
                    }
                    break;
            }
        },
        scrollToHighlighted() {
            this.$nextTick(() => {
                const container = this.$refs.dropdownList?.querySelector('.list_container');
                const highlighted = container?.querySelectorAll('.list_item')[this.selectedIndex];
                
                if (container && highlighted) {
                    const containerTop = container.scrollTop;
                    const containerBottom = containerTop + container.clientHeight;
                    const itemTop = highlighted.offsetTop;
                    const itemBottom = itemTop + highlighted.offsetHeight;
                    
                    if (itemTop < containerTop) {
                        container.scrollTop = itemTop;
                    } else if (itemBottom > containerBottom) {
                        container.scrollTop = itemBottom - container.clientHeight;
                    }
                }
            });
        }
    }
};
</script>

<style scoped>
.country_select_dropdown {
    position: relative;
    display: inline-block;
}

.input_xs {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
    width: 140px;
}

.input_xs:hover {
    border-color: #999;
}

.input_xs.active {
    border-color: #2196F3;
}

.txt {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.chevron {
    margin-left: 8px;
    transition: transform 0.2s;
}

.input_xs.active .chevron {
    transform: rotate(180deg);
}

.dropdown_list {
    background: white;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    max-height: 350px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

.search_box {
    padding: 8px;
    border-bottom: 1px solid #eee;
}

.search_input {
    width: 100%;
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 14px;
    outline: none;
}

.search_input:focus {
    border-color: #2196F3;
}

.list_container {
    flex: 1;
    overflow-y: auto;
    max-height: 280px;
}

.list_item {
    padding: 10px 12px;
    cursor: pointer;
    transition: background-color 0.2s;
    border-bottom: 1px solid #f5f5f5;
}

.list_item:hover {
    background-color: #f5f5f5;
}

.list_item.selected {
    background-color: #e3f2fd;
    font-weight: 500;
}

.list_item.highlighted {
    background-color: #f0f0f0;
}

.list_item.highlighted.selected {
    background-color: #d0e7fc;
}

.country_info {
    font-size: 14px;
}

.no_result {
    padding: 20px;
    text-align: center;
    color: #999;
    font-size: 14px;
}
</style>